import * as React from 'react';

import {DistributionBar} from 'shells/card/distribution-bar/distribution-bar';
import {SegmentedControl} from 'shells/card/segmented-control';
import {colors} from 'shells/colors/colors';
import {FormField} from 'shells/form/form-field';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {useManageSaveState} from 'shells/saved-setting-toast/use-manage-save-state';

import type {ComparisonMetricType, ComparisonEditionType} from 'nutshell-graphql-types';
import type {EditionFragment as Edition} from 'nutshell-graphql-types';

import {useUpdateEdition} from '../../../graphql-wrappers/graphql/hooks/use-update-edition';

import {
    getTestDistributionOptions,
    getDistributionSections,
    WINNER_DECLARED_OPTIONS,
    WINNING_VERSION_OPTIONS,
    getTieBreakerOptions,
} from '../../utils/consts';

import {ContentPreview} from '../content-preview';
import {getComparisonDetails} from '../helpers';

import styles from './a-b-test-details.module.css';

export const REMAINDER = {
    type: 'remainder',
    color: colors.white,
    label: 'Receives winning version',
    labelColor: colors.black,
} as const;

type Props = {
    edition: Edition;
    isABTesting: boolean;
    setShouldShowConfirmation: (arg1: boolean) => void;
    isEditionSent: boolean | null | undefined;
};

export function ABTestDetails(props: Props) {
    const [winningVersion, setWinningVersion] = React.useState(
        props.edition.comparison ? props.edition.comparison.winningMetric : 'OPEN_RATE'
    );
    const [winnerDeclared, setWinnerDeclared] = React.useState(
        props.edition.comparison
            ? props.edition.comparison.testPeriodLength.timespan
            : // @ts-expect-error
              WINNER_DECLARED_OPTIONS[0].value
    );
    const [fallbackEdition, setFallbackEdition] = React.useState(
        props.edition.comparison ? props.edition.comparison.fallbackEdition : 'MAIN'
    );
    const [testDistribution, setTestDistribution] = React.useState(
        props.edition.comparison
            ? props.edition.comparison.sendPercentage
            : // @ts-expect-error
              getTestDistributionOptions(props.edition)[0].value
    );
    const [isSavingConfigDetails, setIsSavingConfigDetails] = React.useState<boolean>(false);
    const {toastState} = useManageSaveState(isSavingConfigDetails, 1000);
    const [savingToastKey, setSavingToastKey] = React.useState<string | null | undefined>(null);

    const comparisonEditionTemplate =
        props.edition.comparisonEdition && props.edition.comparisonEdition.emailTemplate;

    const {updateEdition} = useUpdateEdition();

    const handleSave = (
        input: {
            winningVersion?: ComparisonMetricType;
            winnerDeclared?: number;
            fallbackEdition?: ComparisonEditionType;
            testDistribution?: number;
        },
        toastKey: string
    ) => {
        setIsSavingConfigDetails(true);
        setSavingToastKey(toastKey);
        updateEdition({
            editionId: props.edition.id,
            comparison: {
                winningMetric: input.winningVersion,
                testPeriodLength: input.winnerDeclared
                    ? {unit: 'HH', timespan: input.winnerDeclared}
                    : undefined,
                fallbackEdition: input.fallbackEdition,
                sendPercentage: input.testDistribution,
            },
        }).then(() => {
            setIsSavingConfigDetails(false);
            // Show the saving toast for 1 second
            setTimeout(() => {
                setSavingToastKey(null);
            }, 1000);
        });
    };

    const {comparisonSubject, comparisonPreviewText, comparisonThumbnailUrl} = getComparisonDetails(
        props.edition,
        comparisonEditionTemplate
    );

    const disableConfig = props.isEditionSent || !props.isABTesting;

    return (
        <div className={styles.container}>
            <div className={styles['left-container']}>
                <ContentPreview
                    edition={props.edition}
                    isABTesting={props.isABTesting}
                    setShouldShowConfirmation={props.setShouldShowConfirmation}
                    comparisonSubject={comparisonSubject}
                    comparisonPreviewText={comparisonPreviewText}
                    comparisonThumbnailUrl={comparisonThumbnailUrl}
                />
            </div>
            <div style={{opacity: disableConfig ? 0.6 : 1}} className={styles['right-container']}>
                <StackLegacy spacing={24}>
                    <FormField
                        innerId='winning-version'
                        label='How is the winning version chosen?'
                        sublabel='Select the criteria that will determine a winning version after your test duration.'
                        saveToastState={
                            savingToastKey === 'winning-version' ? toastState : 'initial'
                        }
                    >
                        <SegmentedControl
                            buttonOptions={WINNING_VERSION_OPTIONS}
                            selectedValue={winningVersion}
                            setSelectedValue={setWinningVersion}
                            isDisabled={disableConfig}
                            onChange={(value: ComparisonMetricType) =>
                                handleSave({winningVersion: value}, 'winning-version')
                            }
                        />
                    </FormField>
                    <FormField
                        innerId='test-distribution'
                        label='How would you like to distribute your test?'
                        sublabel='Choose a percentage of recipients for the first round of test messages. The winning version will then be sent to the remaining recipients.'
                        saveToastState={
                            savingToastKey === 'test-distribution' ? toastState : 'initial'
                        }
                    >
                        <StackLegacy spacing={12}>
                            <SegmentedControl
                                buttonOptions={getTestDistributionOptions(props.edition)}
                                selectedValue={testDistribution}
                                setSelectedValue={setTestDistribution}
                                isDisabled={disableConfig}
                                onChange={(value: number) =>
                                    handleSave({testDistribution: value}, 'test-distribution')
                                }
                            />
                            <DistributionBar
                                // @ts-expect-error
                                sections={getDistributionSections(testDistribution)}
                                remainder={REMAINDER}
                            />
                        </StackLegacy>
                    </FormField>
                    <FormField
                        innerId='winner-declared'
                        label='How soon will a winner be declared?'
                        sublabel='After sending your initial batch of messages for comparison, determine how long Nutshell should wait before declaring a winner and sending that version to the rest of your audience.'
                        saveToastState={
                            savingToastKey === 'winner-declared' ? toastState : 'initial'
                        }
                    >
                        <SegmentedControl
                            buttonOptions={WINNER_DECLARED_OPTIONS}
                            selectedValue={winnerDeclared}
                            setSelectedValue={setWinnerDeclared}
                            isDisabled={disableConfig}
                            onChange={(value: number) =>
                                handleSave({winnerDeclared: value}, 'winner-declared')
                            }
                        />
                    </FormField>
                    <FormField
                        innerId='tie-breaker'
                        label="What if there's a tie?"
                        sublabel='In the event of a tie, decide which version should be sent.'
                        saveToastState={savingToastKey === 'tie-breaker' ? toastState : 'initial'}
                    >
                        <SegmentedControl
                            buttonOptions={getTieBreakerOptions({
                                edition: props.edition,
                                comparisonSubject,
                                comparisonPreviewText,
                            })}
                            selectedValue={fallbackEdition}
                            setSelectedValue={(value: ComparisonEditionType) => {
                                setFallbackEdition(value);
                            }}
                            isDisabled={disableConfig}
                            onChange={(value) => {
                                handleSave({fallbackEdition: value}, 'tie-breaker');
                            }}
                        />
                    </FormField>
                </StackLegacy>
            </div>
        </div>
    );
}
